import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import palette from 'core-system/Themes/palette'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import { dashboardActions } from './dashboardActions'
import PeriodSelector from './components/PeriodSelector'
import ProgramEmployeesTable from './components/ProgramEmployeesTable'
import ProgramsStatsGrid from './components/ProgramsStatsGrid'
import ReportedUsageMerchants from './components/ReportedUsageMerchants'
import {
  DashboardPeriod,
  DashboardProgramCardAgency,
} from './dashboardTypes'

const Container = styled.div`
  padding: 2rem;
`

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1rem;
`

const BreadcrumbLink = styled(Link)`
  color: ${palette.primary.pink700};
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
`

const AgencyLogo = styled.img`
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 0.5rem;
  object-fit: contain;
  flex-shrink: 0;
`

const SectionHeader = styled.div`
  margin: 0.5rem 0 1rem;
`

const EmptyState = styled.div`
  padding: 2rem 0;
`

const ProgramDetailView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { launchId: rawLaunchId } = useParams<{ launchId: string }>()
  const launchId = rawLaunchId ? decodeURIComponent(rawLaunchId) : ''
  const [period, setPeriod] = useState<DashboardPeriod>('all')

  const { programDetail, programDetailLoading, programDetailError } =
    useSelector((state: AppState) => state.dashboard)

  useEffect(() => {
    if (launchId) {
      dispatch(dashboardActions.getDashboardProgramDetail(launchId))
    }
    return () => {
      dispatch(dashboardActions.clearDashboardProgramDetail())
    }
  }, [dispatch, launchId])

  if (!programDetail) {
    return (
      <Container>
        <EmptyState>
          <Text variant='body1' color={palette.text.secondary}>
            {programDetailError
              ? 'Program not found'
              : programDetailLoading || launchId
                ? 'Loading program…'
                : 'Program not found'}
          </Text>
        </EmptyState>
      </Container>
    )
  }

  const isTransitPass = programDetail.types.includes('TRANSIT_PASS')
  const isReportedUsage = programDetail.types.includes('REPORTED_USAGE')
  const agencies: DashboardProgramCardAgency[] = isTransitPass
    ? programDetail.transitAgencies || []
    : []
  const periodStats = programDetail.byPeriod?.[period]
  const merchantLabels = (programDetail.reportedMerchants || []).map(
    (merchant) => ({
      id: merchant,
      label:
        periodStats?.merchants?.find((item) => item.merchant === merchant)
          ?.label || merchant,
    })
  )

  return (
    <Container data-cy='program-detail-view'>
      <Breadcrumb>
        <BreadcrumbLink to={Locations.Dashboard}>Dashboard</BreadcrumbLink>
        <Text variant='caption' color={palette.text.secondary}>
          /
        </Text>
        <Text variant='caption' color={palette.text.secondary}>
          {programDetail.superProgramName}
        </Text>
      </Breadcrumb>

      <HeaderRow>
        <TitleGroup>
          <Text variant='h1' fontWeight={600}>
            {programDetail.superProgramName}
          </Text>
          {agencies.map((agency) =>
            agency.iconUrl ? (
              <AgencyLogo
                key={agency.id}
                src={agency.iconUrl}
                alt={agency.name}
              />
            ) : null
          )}
        </TitleGroup>
        <HeaderActions>
          <PeriodSelector value={period} onChange={setPeriod} />
          {isReportedUsage ? (
            <SecondaryButton
              onClick={() =>
                navigate(
                  Locations.DashboardProgramData.replace(
                    ':launchId',
                    encodeURIComponent(launchId)
                  )
                )
              }
            >
              Upload data
            </SecondaryButton>
          ) : null}
          {isReportedUsage ? (
            <SecondaryButton
              onClick={() =>
                navigate(
                  Locations.DashboardProgramConnections.replace(
                    ':launchId',
                    encodeURIComponent(launchId)
                  )
                )
              }
            >
              Connections
            </SecondaryButton>
          ) : null}
        </HeaderActions>
      </HeaderRow>

      <ProgramsStatsGrid
        statsByPeriod={programDetail.byPeriod}
        period={period}
        showTitle={false}
        moneyLabel={isTransitPass ? 'Topups' : 'Spend'}
        activeLabel={isReportedUsage ? 'Records' : 'Active Cards'}
        pendingLabel={isReportedUsage ? 'Unmatched rows' : 'Pending Activation'}
        showPending={!isTransitPass}
      />

      {isReportedUsage ? (
        <ReportedUsageMerchants
          merchants={periodStats?.merchants || []}
          period={period}
        />
      ) : null}

      <SectionHeader>
        <Text variant='h3' fontWeight={500}>
          Employees
        </Text>
      </SectionHeader>
      <ProgramEmployeesTable
        employees={programDetail.employees}
        period={period}
        isTransitPass={isTransitPass}
        merchants={isReportedUsage ? merchantLabels : []}
      />
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ProgramDetailView.displayName = 'ProgramDetailView'
}

export default ProgramDetailView
